import React from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, TextField, Button,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import SectionCard from '../ui/SectionCard';
import { Row, EditableRow, EditableAutocompleteRow } from '../dashboard/Row';
import { useOpportunityDashboard } from '../../hooks/useOpportunityDashboard';
import { formatDate } from '../../utils/format';
import { TYPE_LABELS, TYPE_OPTIONS } from '../../constants/type';

const TYPE_SELECT_OPTIONS = TYPE_OPTIONS.map((t) => ({ value: t, label: TYPE_LABELS[t] }));

// งบประมาณอนุมัติแล้ว — boolean ล้วนๆ (อนุมัติ/ไม่อนุมัติ) ไม่เก็บจำนวนเงินแยกต่างหากอีกต่อไป
// (ก่อนหน้านี้เคยให้กรอกยอดอนุมัติเองได้ อิสระจาก budget — ตัดออกตามที่ user ขอ) สลับสถานะได้
// ด้วยปุ่มเดียว ไม่มีค่าอื่นให้กรอกเพิ่ม
function BudgetApproveField({ approved, onSave }) {
  return (
    <Box sx={{ py: 0.4 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{ width: 150, flexShrink: 0, fontSize: '0.78rem', color: '#6b7280' }}>
          งบประมาณอนุมัติแล้ว
        </Typography>
        {approved ? (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a', fontSize: '0.8rem' }}>
            อนุมัติ
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
            ยังไม่อนุมัติ
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          color={approved ? 'inherit' : 'success'}
          onClick={() => onSave(!approved)}
          sx={{ minWidth: 0, px: 1, py: 0.1, fontSize: '0.7rem' }}
        >
          {approved ? 'ยกเลิกอนุมัติ' : 'อนุมัติ'}
        </Button>
      </Stack>
    </Box>
  );
}

// กรอบรวมฟิลด์ที่มีความหมายเดียวกัน (ข้อมูลโครงการ/ผู้เกี่ยวข้อง/วันที่/ราคา/บันทึก) — หัวกลุ่มเล็กจางเหนือเส้น
// ไม่แข่งกับหัวการ์ดหลัก
function FieldGroup({ title, children }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        pt: 1,
        pb: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4, mb: 0.25,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

// แผงข้อมูลทั่วไป — สไตล์ inline-edit เดียวกับ ProjectSingleDashboard.js (P111) ของโมดูล
// Project: คลิกค่าเพื่อแก้ กรอบเส้นบางบอกว่าแก้ได้ ไม่มีปุ่ม "แก้ไข" แยก/ไม่เปิด dialog
// ต่างหากอีกต่อไป (ดู projectSingleDashboard/components/Row.js ต้นแบบ) รวมทุกฟิลด์ที่มีทั้ง
// ในตารางลิสต์และฟอร์มสร้างโครงการไว้ในที่เดียว — สถานะยังคงแก้ผ่าน StatusActionsCard เดิม
// (ไม่ทำซ้ำที่นี่ กันมี UI แก้สถานะสองจุด)
//
// จัดฟิลด์เป็น 4 กลุ่มตามความหมาย (ตามที่ user ขอ) แทนลำดับเดิมที่เรียงตามคอลัมน์ใน OpportunitiesTable.js —
// "สถานะ" ยังไม่ซ้ำที่นี่ (แก้ผ่าน StatusActionsCard) และหมายเหตุ/Note ย้ายไปการ์ดแยก NoteCard.js
// (คอลัมน์ขวา เหนือการ์ดสถานะโครงการ)
export function OpportunityInfoCard({
  opportunity, onChanged, onLocalUpdate, showSnack,
}) {
  const {
    contactInput, setContactInput, contactOptions,
    ownerInput, setOwnerInput, ownerOptions, ownerName,
    orderedByInput, setOrderedByInput, orderedByName, creatorName,
    newAppointmentDate, setNewAppointmentDate,
    newAppointmentTitle, setNewAppointmentTitle,
    patchField, addAppointment, removeAppointment,
  } = useOpportunityDashboard({
    opportunity, onChanged, onLocalUpdate, showSnack,
  });

  const appointments = opportunity.marketing_appointments || [];

  return (
    <SectionCard icon={<InfoOutlinedIcon />} title="ข้อมูลทั่วไป" accentColor="primary.main">
      <Stack spacing={1.5}>
        <FieldGroup title="ข้อมูลโครงการ">
          <Row label="เลขที่โครงการ" value={opportunity.opportunity_number} bold />

          <EditableRow
            label="ชื่อโครงการ"
            value={opportunity.project_name}
            displayValue={opportunity.project_name}
            onSave={(v) => patchField({ projectName: v })}
          />

          <EditableRow
            label="Type"
            type="select"
            options={TYPE_SELECT_OPTIONS}
            value={opportunity.type}
            displayValue={opportunity.type ? TYPE_LABELS[opportunity.type] : null}
            onSave={(v) => patchField({ type: v })}
          />

          {opportunity.type === 'OTHER' && (
            <EditableRow
              label="ระบุประเภทงาน (Other)"
              value={opportunity.type_other}
              displayValue={opportunity.type_other}
              onSave={(v) => patchField({ typeOther: v })}
            />
          )}

          <EditableRow
            label="Work Description"
            type="textarea"
            value={opportunity.detail}
            displayValue={opportunity.detail}
            onSave={(v) => patchField({ detail: v })}
          />
        </FieldGroup>

        <FieldGroup title="ผู้เกี่ยวข้อง">
          <EditableAutocompleteRow
            label="ลูกค้า"
            displayValue={opportunity.contact_name_snapshot}
            options={contactOptions}
            value={opportunity.contact_id ? { _id: opportunity.contact_id, name: opportunity.contact_name_snapshot } : null}
            inputValue={contactInput}
            onInputChange={setContactInput}
            onChange={(v) => {
              if (!v) return;
              patchField({ contactId: v._id || v.id, contactNameSnapshot: v.name });
            }}
          />

          <EditableAutocompleteRow
            label="ผู้ประสานงาน"
            displayValue={orderedByName}
            options={ownerOptions}
            value={opportunity.ordered_by ? { _id: opportunity.ordered_by, name: orderedByName } : null}
            inputValue={orderedByInput}
            onInputChange={setOrderedByInput}
            onChange={(v) => patchField({ orderedById: v ? (v._id || v.id) : null })}
          />

          <EditableAutocompleteRow
            label="ผู้รับผิดชอบ (Owner)"
            displayValue={ownerName}
            options={ownerOptions}
            value={opportunity.owner_id ? { _id: opportunity.owner_id, name: ownerName } : null}
            inputValue={ownerInput}
            onInputChange={setOwnerInput}
            onChange={(v) => patchField({ ownerId: v ? (v._id || v.id) : null })}
          />

          <Row label="ผู้สร้าง" value={creatorName || null} />
        </FieldGroup>

        <FieldGroup title="วันที่">
          <Row label="วันที่สร้างโครงการ" value={formatDate(opportunity.date_start)} />

          <EditableRow
            label="วันครบกำหนดยื่นใบเสนอราคา"
            type="date"
            value={opportunity.quotation_due_date ? dayjs(opportunity.quotation_due_date).format('YYYY-MM-DD') : ''}
            displayValue={formatDate(opportunity.quotation_due_date)}
            onSave={(v) => patchField({ quotationDueDate: v })}
          />

          <Box sx={{ py: 0.6 }}>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.78rem', color: '#6b7280', mb: 0.5 }}>
              วันที่นัด
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
              {appointments.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>ยังไม่มีวันที่นัด</Typography>
              )}
              {appointments.map((a) => (
                <Chip
                  key={a.id || a.date}
                  size="small"
                  label={a.title ? `${dayjs(a.date).format('DD/MM/YYYY')} — ${a.title}` : dayjs(a.date).format('DD/MM/YYYY')}
                  onDelete={() => removeAppointment(a.id)}
                  deleteIcon={<DeleteOutlineIcon />}
                />
              ))}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                size="small" type="date"
                value={newAppointmentDate}
                onChange={(e) => setNewAppointmentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small" label="นัดเรื่องอะไร" placeholder="เช่น นัดพบลูกค้า"
                value={newAppointmentTitle}
                onChange={(e) => setNewAppointmentTitle(e.target.value)}
              />
              <IconButton color="primary" onClick={addAppointment} disabled={!newAppointmentDate} sx={{ mt: 0.5 }}>
                <AddIcon />
              </IconButton>
            </Box>
          </Box>

          <Row label="วันที่ปิดงาน" value={formatDate(opportunity.date_end)} />
        </FieldGroup>

        <FieldGroup title="ราคาและงบประมาณ">
          <EditableRow
            label="ราคาที่เสนอ"
            type="number"
            value={opportunity.value}
            displayValue={opportunity.value != null ? Number(opportunity.value).toLocaleString('th-TH') : null}
            onSave={(v) => patchField({ value: v })}
          />

          <EditableRow
            label="งบประมาณ (Budget)"
            type="number"
            value={opportunity.budget}
            displayValue={opportunity.budget != null ? Number(opportunity.budget).toLocaleString('th-TH') : null}
            onSave={(v) => patchField({ budget: v })}
          />

          <BudgetApproveField
            approved={opportunity.budget_approve}
            onSave={(v) => patchField({ budgetApprove: v })}
          />
        </FieldGroup>
      </Stack>
    </SectionCard>
  );
}

export default OpportunityInfoCard;
